import type {Evaluator} from "@cm/graph-v3"
import type {OnCompileContext, OnFilterActiveContext, TemplateNodeContext, TemplateNodeDefinition, VisitorNodeResult} from "#template/template-node/types"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import {graphNode} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import {TemplateNode} from "#template/template-node/template-node"
import {TemplateNodes} from "#template/nodes/template-nodes"
import {colorValue, externalId} from "#template/nodes/validation"
import type {ColorValue, ExternalId} from "#template/nodes/types"
import {DataObjectReference} from "#template/nodes/image/data-object-reference"
import type {NodeOwnerLikeNode} from "#template/role-markers"
import {nodeOwnerMarker} from "#template/role-markers"
import {visitNone, visitAll, visit} from "#template/template-node/visit-helpers"

export type ConfigVariantParameters = {
    name: string
    id: ExternalId
    nodes: TemplateNodes
    iconDataObject?: DataObjectReference
    iconColor?: ColorValue
}

const configVariantDefinition: TemplateNodeDefinition<ConfigVariantParameters> = {
    nodeClass: "ConfigVariant",
    validation: {
        parameters: z.object({
            name: z.string(),
            id: externalId,
            nodes: graphNode(TemplateNodes),
            iconDataObject: z.optional(graphNode(DataObjectReference)),
            iconColor: z.optional(colorValue),
        }),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class ConfigVariant extends TemplateNode<ConfigVariantParameters> implements NodeOwnerLikeNode {
    get [nodeOwnerMarker]() {
        return true as const
    }

    public override get definition() {
        return configVariantDefinition
    }

    public override onFilterActive(_context: OnFilterActiveContext, evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<ConfigVariantParameters> {
        const {nodes, ...rest} = this.parameters
        return {
            nodes: visit(nodes, evaluator),
            ...visitNone(rest),
        }
    }

    public override onCompile(_context: OnCompileContext, evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<ConfigVariantParameters> {
        return visitAll(this.parameters, evaluator)
    }
}
